/**
 * Modal/popup header shared by dialogs across the app: tinted icon badge, title and an optional subtitle line.
 */

import React from 'react';
import { Flex, Typography, theme } from 'antd';

const { Text } = Typography;

interface ModalTitleProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  /** Secondary line, typically "client name · date/time". */
  subtitle?: React.ReactNode;
  /** Colour of the icon badge; defaults to the primary colour. */
  tone?: 'primary' | 'neutral';
}

/** Renders the shared dialog header (icon badge + title + optional subtitle). */
export const ModalTitle: React.FC<ModalTitleProps> = ({
  icon,
  title,
  subtitle,
  tone = 'primary'
}) => {
  const { token } = theme.useToken();
  const badgeSize = token.controlHeight;

  return (
    <Flex align="center" gap={token.marginSM} style={{ minWidth: 0 }}>
      <Flex
        align="center"
        justify="center"
        aria-hidden
        style={{
          width: badgeSize,
          height: badgeSize,
          borderRadius: token.borderRadiusLG,
          background: tone === 'primary' ? token.colorPrimaryBg : token.colorFillSecondary,
          color: tone === 'primary' ? token.colorPrimary : token.colorTextSecondary,
          fontSize: token.fontSizeLG,
          flexShrink: 0
        }}
      >
        {icon}
      </Flex>
      <Flex vertical style={{ minWidth: 0 }}>
        <Text strong style={{ fontSize: token.fontSizeLG, lineHeight: token.lineHeightLG }}>
          {title}
        </Text>
        {subtitle ? (
          <Text type="secondary" ellipsis style={{ fontSize: token.fontSizeSM, fontWeight: 'normal' }}>
            {subtitle}
          </Text>
        ) : null}
      </Flex>
    </Flex>
  );
};
